// src/components/layout/Footer.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  Mail,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Heart,
  ExternalLink,
  CheckCircle,
  Loader2
} from 'lucide-react';
import ShareButton from '../shared/ShareButton';
import { DDD_PRIVACY_URL, DDD_TERMS_URL, DDD_COOKIES_URL } from '../../config/legalLinks';

const EMAIL_HUB_URL = 'https://us-central1-ddd-email-hub.cloudfunctions.net';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('idle'); // idle, loading, success, error
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setSubscribeStatus('error');
      setSubscribeMessage('Please enter a valid email');
      return;
    }

    setSubscribeStatus('loading');

    try {
      const response = await fetch(`${EMAIL_HUB_URL}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand: 'ddd',
          email: email,
          source: 'relationship-compass-footer'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubscribeStatus('success');
        setSubscribeMessage('Thanks for subscribing!');
        setEmail('');
      } else {
        setSubscribeStatus('error');
        setSubscribeMessage(data.error || 'Subscription failed. Please try again.');
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMessage('Network error. Please try again.');
    }
  };

  const dddApps = [
    {
      name: 'The Art of Story',
      href: 'https://artofstory.divedeepdialogues.com',
      description: 'Master magnetic storytelling'
    },
    {
      name: 'Deeper Dialogues',
      href: 'https://deeperdialogues.divedeepdialogues.com',
      description: 'Our flagship relationship app'
    },
    {
      name: 'The Art of Conversation',
      href: 'https://artofconversation.divedeepdialogues.com',
      description: 'Master meaningful dialogue'
    },
    {
      name: 'Inner Voice Mastery',
      href: 'https://innervoice.divedeepdialogues.com',
      description: 'Transform your self-talk'
    },
    {
      name: 'Dive Deep Workplace',
      href: 'https://divedeepworkplace.com',
      description: 'Better conversations at work'
    }
  ];

  const quickLinks = [
    { name: 'Assessment', href: '/assessment' },
    { name: 'Learning Modules', href: '/modules' },
    { name: 'Toolkit', href: '/toolkit' },
    { name: 'Help & Support', href: '/resources' },
    { name: 'About DDD', href: 'https://divedeepdialogues.com', external: true },
    { name: 'Feedback / Report Bugs', href: 'https://divedeepdialogues.com/feedback', external: true, icon: 'feedback' },
    { name: 'Data & Privacy Settings', href: '/privacy' },
    // Legal policies are DDD-wide (src/config/legalLinks.js)
    { name: 'Privacy Policy', href: DDD_PRIVACY_URL, external: true },
    { name: 'Terms of Use', href: DDD_TERMS_URL, external: true },
    { name: 'Cookie Policy', href: DDD_COOKIES_URL, external: true }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61565456445914' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/dive-deep-dialogues' }
  ];

  return (
    <footer className="relative mt-20 border-t border-slate-800">
      <div className="container mx-auto px-4 py-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Column */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4 group">
              <img
                src="/relationship-compass-logo.png"
                alt="Relationship Compass"
                className="w-12 h-12 object-contain group-hover:scale-110 transition-transform"
              />
              <div className="flex flex-col">
                <h3 className="text-white text-sm font-bold group-hover:text-rose-400 transition-colors" style={{ lineHeight: '1.2' }}>Relationship Compass</h3>
                <p className="text-xs text-slate-400 group-hover:text-rose-300 transition-colors" style={{ lineHeight: '1', marginTop: '2px' }}>by Dive Deep Dialogues</p>
              </div>
            </Link>
            <p className="text-sm text-slate-400 mb-4">
              Navigate your relationship with clarity through deeper dialogues. Strengthen connection, deepen understanding, and build the lasting resilience that comes from being seen and heard.
            </p>

            {/* Social Links */}
            <div className="flex gap-2">
              {socialLinks.map(social => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-white/10 hover:bg-rose-500/20 rounded-lg flex items-center justify-center transition-colors"
                    aria-label={social.name}
                  >
                    <Icon className="w-4 h-4 text-slate-400 hover:text-rose-400" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Other DDD Apps */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Explore Our Apps</h4>
            <ul className="space-y-3">
              {dddApps.map(app => (
                <li key={app.name}>
                  <a
                    href={app.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <div className="flex items-start gap-1">
                      <span className="text-sm text-white group-hover:text-rose-400 transition-colors font-medium">
                        {app.name}
                      </span>
                      <ExternalLink className="w-3 h-3 text-slate-400 mt-0.5" />
                    </div>
                    <p className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors" style={{ marginTop: '2px' }}>
                      {app.description}
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map(link => (
                <li key={link.name}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-400 hover:text-rose-400 text-sm transition-colors inline-flex items-center gap-1 py-1"
                    >
                      {link.icon === 'feedback' && <MessageSquare className="w-3.5 h-3.5" />}
                      {link.name}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-slate-400 hover:text-rose-400 text-sm transition-colors inline-block py-1"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Stay Connected</h4>
            <p className="text-xs text-slate-400 mb-3">
              Get weekly relationship insights and tools
            </p>
            {subscribeStatus === 'success' ? (
              <div className="flex items-center gap-2 text-emerald-400 text-sm py-2">
                <CheckCircle className="w-4 h-4" />
                <span>{subscribeMessage}</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  disabled={subscribeStatus === 'loading'}
                  className="w-full px-3 py-2 text-sm bg-white/10 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-rose-500 disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={subscribeStatus === 'loading'}
                  className="w-full px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {subscribeStatus === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    'Subscribe'
                  )}
                </button>
                {subscribeStatus === 'error' && (
                  <p className="text-rose-400 text-xs">{subscribeMessage}</p>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-slate-800">
          <p className="text-xs text-slate-400 text-center md:text-left mb-4">
            Relationship Compass is educational skills training, not therapy, counselling or crisis support. If you or someone else is in danger, see{' '}
            <Link to="/resources" className="text-rose-300 hover:text-white underline">Help &amp; Support</Link>{' '}
            or call your local emergency number.
          </p>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-400">
              © {currentYear} Relationship Compass by SILVER WOLF Productions
            </p>

            <p className="text-xs text-slate-400 flex items-center gap-1">
              Built with <Heart className="w-3 h-3 text-rose-400" /> in Australia
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
